import React, { useState } from 'react';
import { 
  Container, 
  Title, 
  Group, 
  TextInput, 
  Select, 
  Button, 
  Card, 
  Text, 
  Badge, 
  ActionIcon, 
  Pagination,
  LoadingOverlay
} from '@mantine/core';
import { 
  IconSearch, 
  IconPlus, 
  IconRefresh, 
  IconEdit, 
  IconTrash 
} from '@tabler/icons-react';
import { useNavigate } from '@tanstack/react-router';
import { useProducts, useDeleteProduct, useRefreshProducts } from '../../hooks/use-products';
import { useAuthorization } from '../../hooks/use-authorization';

export function ProductsPage() {
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const [category, setCategory] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const pageSize = 10;

  // Permessi
  const { checkPermission } = useAuthorization();
  
  // Verifica i permessi per le azioni sui prodotti da utilizzare poi nelle varie operazioni
  const canAddProduct = checkPermission('add-product');
  const canEditProduct = checkPermission('edit-product');
  const canDeleteProduct = checkPermission('delete-product');
  
  // Utilizza l'hook useProducts per ottenere i dati
  const { data, isLoading, error } = useProducts({
    page,
    pageSize,
    search: search || undefined,
    category: category || undefined,
    sortBy: 'updatedAt',
    sortDirection: 'desc'
  });
  
  // Mutation per eliminare prodotti
  const deleteProduct = useDeleteProduct();
  
  // Hook per ricaricare manualmente i dati
  const { refreshAllProducts } = useRefreshProducts();
  
  const handleSearch = () => {
    setPage(1); // Torna alla prima pagina quando si esegue una nuova ricerca
  };
  
  const handleDelete = (id: number) => {
    if (window.confirm('Sei sicuro di voler eliminare questo prodotto?')) {
      deleteProduct.mutate(id);
    }
  };
  
  const handleRefresh = () => {
    refreshAllProducts();
  };
  
  if (error) {
    return (
      <Container size="xl" py="xl">
        <Card withBorder p="xl" mt="md">
          <Text color="red" ta="center">
            Si è verificato un errore nel caricamento dei prodotti.
          </Text>
        </Card>
      </Container>
    );
  }
  
    function formatCurrency(price: number): React.ReactNode {
        return new Intl.NumberFormat('it-IT', {
            style: 'currency',
            currency: 'EUR',
        }).format(price);
    }

  return (
    <Container size="xl" py="xl">
      <Group justify="apart" mb="lg">
        <Title order={2}>Gestione Prodotti</Title>
        <Button 
          leftSection={<IconPlus size={16} />} 
          onClick={() => navigate({ to: '/products/new' })}
        >
          Nuovo Prodotto
        </Button>
      </Group>
      
      {/* Filtri */}
      <Card withBorder p="md" mb="xl">
        <Group>
          <TextInput
            placeholder="Cerca prodotti..."
            value={search}
            onChange={(e) => setSearch(e.currentTarget.value)}
            leftSection={<IconSearch size={16} />}
            style={{ flex: 1 }}
          />
          
          <Select
            placeholder="Categoria"
            clearable
            value={category}
            onChange={setCategory}
            data={[
              { value: 'elettronica', label: 'Elettronica' },
              { value: 'abbigliamento', label: 'Abbigliamento' },
              { value: 'casa', label: 'Casa' },
              { value: 'sport', label: 'Sport' },
            ]}
            style={{ width: 180 }}
          />
          
          <Button onClick={handleSearch}>Cerca</Button>
          
          <ActionIcon 
            variant="light" 
            color="blue" 
            size="lg" 
            onClick={handleRefresh}
            title="Ricarica dati"
          >
            <IconRefresh size={18} />
          </ActionIcon>
        </Group>
      </Card>
      
      {/* Lista prodotti */}
      <Card withBorder p={0} pos="relative">
        <LoadingOverlay visible={isLoading} />
        
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ borderBottom: '1px solid #dee2e6' }}>
              <th style={{ textAlign: 'left', padding: '12px 16px' }}>ID</th>
              <th style={{ textAlign: 'left', padding: '12px 16px' }}>Nome</th>
              <th style={{ textAlign: 'left', padding: '12px 16px' }}>Categoria</th>
              <th style={{ textAlign: 'right', padding: '12px 16px' }}>Prezzo</th>
              <th style={{ textAlign: 'center', padding: '12px 16px' }}>Disponibilità</th>
              <th style={{ textAlign: 'right', padding: '12px 16px' }}>Azioni</th>
            </tr>
          </thead>
          <tbody>
            {data?.items && data.items.length > 0 ? (
              data.items.map((product) => (
                <tr key={product.id} style={{ borderBottom: '1px solid #dee2e6' }}>
                  <td style={{ padding: '12px 16px' }}>{product.id}</td>
                  <td style={{ padding: '12px 16px' }}>{product.name}</td>
                  <td style={{ padding: '12px 16px' }}>
                    <Badge color="blue" style={{ color: 'white' }}>{product.category}</Badge>
                  </td>
                  <td style={{ padding: '12px 16px', textAlign: 'right' }}>
                    {formatCurrency(product.price)}
                  </td>
                  <td style={{ padding: '12px 16px', textAlign: 'center' }}>
                    <Badge color={product.inStock ? 'green' : 'red'} style={{ color: 'white' }}>
                      {product.inStock ? 'Disponibile' : 'Esaurito'}
                    </Badge>
                  </td>
                  <td style={{ padding: '12px 16px', textAlign: 'right' }}>
                    <Group gap={8} justify="right">
                      <ActionIcon
                        color="blue"
                        onClick={() => navigate({ to: `/products/${product.id}` })}
                      >
                        <IconEdit size={16} />
                      </ActionIcon>
                      <ActionIcon
                        color="red"
                        onClick={() => handleDelete(product.id)}
                        loading={deleteProduct.isPending}
                      >
                        <IconTrash size={16} />
                      </ActionIcon>
                    </Group>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan={6} style={{ textAlign: 'center', padding: '32px 16px' }}>
                  {isLoading ? 'Caricamento...' : 'Nessun prodotto trovato'}
                </td>
              </tr>
            )}
          </tbody>
        </table>
        
        {data?.pagination && data.pagination.totalPages > 1 && (
          <Group justify="center" p="md">
            <Pagination
              total={data.pagination.totalPages}
              value={page}
              onChange={setPage}
            />
          </Group>
        )}
      </Card>
    </Container>
  );
}